import { Injectable } from '@angular/core';
import { Action as ActionSelector, Selector, State, StateContext } from '@ngxs/store';
import { iif, insertItem, patch, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ActionsService } from 'src/app/services/actions.service';
import { ActionForm } from 'src/app/services/dtos/actionForm';
import { ActionWithSettings } from 'src/app/services/dtos/actionWithSettings';
// eslint-disable-next-line max-len
import { AddActionLaboratorySpecificSetting, ClearCurrentActionId, DeleteActionLaboratorySpecificSetting, FetchAction, FetchAllActionsBasicInfo, SetCurrentActionId, UpdateAction, UpdateActionLaboratorySpecificSetting } from './actions.actions';

export class ActionsStateModel {
  public actions!: ActionWithSettings[];
  public selectedActionId!: string;
}

const defaults = {
  actions: [],
  selectedActionId: ''
};

@State<ActionsStateModel>({
  name: 'actionsState',
  defaults
})

@Injectable()
export class ActionsState {
  public constructor(private actionsService: ActionsService) { }

  @Selector()
  public static getAllActions(state: ActionsStateModel): ActionWithSettings[] {
    return state.actions;
  }

  @Selector()
  public static getSelectedAction(state: ActionsStateModel): ActionWithSettings | undefined {
    return state.actions.find(c => c.id === state.selectedActionId);
  }

  @ActionSelector(SetCurrentActionId)
  public setCurrentActionId({ setState }: StateContext<ActionsStateModel>, { id }: SetCurrentActionId): void {
    setState(
      patch({
        selectedActionId: id,
      })
    );
  }

  @ActionSelector(ClearCurrentActionId)
  public clearCurrentActionId({ setState }: StateContext<ActionsStateModel>): void {
    setState(
      patch({
        selectedActionId: ''
      })
    );
  }

  @ActionSelector(FetchAllActionsBasicInfo)
  public fetchAll({ getState, setState }: StateContext<ActionsStateModel>): Observable<ActionWithSettings[]> {
    return this.actionsService.getAllBasicInfo()
      .pipe(
        tap((actions: ActionWithSettings[]) => {
          const state = getState();
          setState({
            ...state,
            actions
          });
        })
      );
  }

  @ActionSelector(FetchAction)
  public fetchSingle({ setState }: StateContext<ActionsStateModel>, { id }: FetchAction): Observable<ActionWithSettings> {
    return this.actionsService.getAction(id)
      .pipe(
        tap((action: ActionWithSettings) => {
          setState(
            patch<ActionsStateModel>({
              actions: iif<ActionWithSettings[]>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                actions => actions!.some(x => x.id === action.id),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                updateItem<ActionWithSettings>(x => x!.id === action.id, action),
                insertItem<ActionWithSettings>(action)
              )
            })
          );
        })
      );
  }

  @ActionSelector(UpdateAction)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public update({ dispatch }: StateContext<ActionsStateModel>, { payload }: UpdateAction): Observable<any> {
    return this.actionsService.updateAction(payload as ActionForm)
      .pipe(
        tap(() => {
          dispatch(new FetchAction(payload.id));
        })
      );
  }

    @ActionSelector(AddActionLaboratorySpecificSetting)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public addActionLaboratorySpecificSetting({ dispatch }: StateContext<ActionsStateModel>, { payload }: AddActionLaboratorySpecificSetting): Observable<any> {
    return this.actionsService.addActionLaboratorySpecificSetting(payload)
      .pipe(
        tap(() => {
          dispatch(new FetchAction(payload.contextModelId));
        })
      );
  }

  @ActionSelector(UpdateActionLaboratorySpecificSetting)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public updateActionLaboratorySpecificSetting({ dispatch }: StateContext<ActionsStateModel>, { payload }: UpdateActionLaboratorySpecificSetting): Observable<any> {
    return this.actionsService.updateActionLaboratorySpecificSetting(payload)
      .pipe(
        tap(() => {
          dispatch(new FetchAction(payload.contextModelId));
        })
      );
  }

  @ActionSelector(DeleteActionLaboratorySpecificSetting)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
  public deleteActionLaboratorySpecificSetting({ dispatch }: StateContext<ActionsStateModel>, { actionId, settingId }: DeleteActionLaboratorySpecificSetting): Observable<any> {
    return this.actionsService.deleteActionLaboratorySpecificSetting(actionId, settingId)
      .pipe(
        tap(() => {
          dispatch(new FetchAction(actionId));
        })
      );
  }

}
